import React from 'react';
import MockProductImage from '@/components/brand/MockProductImage';

/**
 * Artwork for a product category, shown when a product has no photo of
 * its own (the NAFDAC sample catalogue has none). Inline SVG: nothing
 * to download, works offline, same flat style and palette as
 * MockProductImage. Categories without a drawing fall back to one of
 * MockProductImage's bottle/bag variants.
 */
export default function CategoryArt({ category, variantKey, className = '' }) {
  const art = ART[category];
  // The drawing is square; the wrapper's colour fills the rest of a wide
  // slot (marketplace cards) so it doesn't sit between white bands.
  if (!art) {
    return (
      <div className={`w-full h-full ${className}`} style={{ backgroundColor: FALLBACK_BG }}>
        <MockProductImage variantKey={variantKey} />
      </div>
    );
  }
  return (
    <div className={`w-full h-full ${className}`} style={{ backgroundColor: art.bg }}>
      <svg
        viewBox="0 0 108 108"
        preserveAspectRatio="xMidYMid meet"
        className="w-full h-full block"
        role="img"
        aria-hidden="true"
        data-testid={`category-art-${category}`}
      >
        <rect x="0" y="0" width="108" height="108" fill={art.bg} />
        {art.shape}
      </svg>
    </div>
  );
}

const FALLBACK_BG = '#EEF2EA';

const ART = {
  // Vial with a rubber stopper, and a syringe beside it.
  vaccine: {
    bg: '#DCEBF3',
    shape: (
      <g>
        <rect x="30" y="20" width="22" height="8" rx="2" fill="#2E6F95" />
        <rect x="32" y="28" width="18" height="6" fill="#9CC3DA" />
        <rect x="28" y="34" width="26" height="54" rx="5" fill="#F4FAFD" stroke="#2E6F95" strokeWidth="1.8" />
        <rect x="28" y="58" width="26" height="30" rx="5" fill="#7FB3D3" />
        <rect x="32" y="44" width="18" height="10" rx="1.5" fill="#2E6F95" />
        <g transform="rotate(35 74 58)">
          <rect x="70" y="26" width="8" height="46" rx="2" fill="#FFFFFF" stroke="#2E6F95" strokeWidth="1.6" />
          <rect x="70" y="50" width="8" height="22" rx="2" fill="#7FB3D3" />
          <rect x="66" y="22" width="16" height="4" rx="1" fill="#2E6F95" />
          <rect x="72.5" y="14" width="3" height="9" fill="#2E6F95" />
          <rect x="73.3" y="72" width="1.4" height="12" fill="#5A6B75" />
        </g>
      </g>
    ),
  },
  // Medicine bottle with a cross on the label, and two tablets.
  veterinary_drug: {
    bg: '#E2EFE4',
    shape: (
      <g>
        <rect x="38" y="16" width="26" height="10" rx="2" fill="#2F6B3D" />
        <rect x="34" y="26" width="34" height="8" rx="2" fill="#BFD9C4" />
        <rect x="30" y="34" width="42" height="56" rx="7" fill="#F6FBF7" stroke="#2F6B3D" strokeWidth="1.8" />
        <rect x="34" y="48" width="34" height="28" rx="2" fill="#2F6B3D" />
        <rect x="48" y="53" width="6" height="18" fill="#FFFFFF" />
        <rect x="42" y="59" width="18" height="6" fill="#FFFFFF" />
        <ellipse cx="80" cy="84" rx="7" ry="4.5" fill="#FFFFFF" stroke="#2F6B3D" strokeWidth="1.4" />
        <ellipse cx="90" cy="78" rx="6" ry="4" fill="#BFD9C4" stroke="#2F6B3D" strokeWidth="1.4" />
      </g>
    ),
  },
  // Fertiliser sack, tied at the top.
  fertilizer: {
    bg: '#F2E6CF',
    shape: (
      <g>
        <path d="M30 28 Q54 20 78 28 L84 92 Q54 98 24 92 Z" fill="#E7CF9F" stroke="#7A5A1E" strokeWidth="1.8" />
        <path d="M42 22 L66 22 L62 30 L46 30 Z" fill="#7A5A1E" />
        <rect x="36" y="48" width="36" height="26" rx="2" fill="#3E6A46" />
        <text x="54" y="65" textAnchor="middle" fontFamily="Outfit, sans-serif" fontWeight="700" fontSize="10" fill="#FFFFFF">NPK</text>
      </g>
    ),
  },
  // Seed packet with seeds.
  seed: {
    bg: '#EEF0D5',
    shape: (
      <g>
        <rect x="30" y="20" width="48" height="68" rx="3" fill="#FFFDF2" stroke="#6B7A2A" strokeWidth="1.8" />
        <path d="M30 28 L78 28" stroke="#6B7A2A" strokeWidth="1.5" strokeDasharray="3 3" />
        <path d="M54 46 Q44 54 54 72 Q64 54 54 46 Z" fill="#8FA33B" />
        <path d="M54 50 L54 72" stroke="#5B6B1F" strokeWidth="1.4" />
        <ellipse cx="40" cy="80" rx="3" ry="2" fill="#B08A3E" />
        <ellipse cx="68" cy="80" rx="3" ry="2" fill="#B08A3E" />
      </g>
    ),
  },
  // Trigger sprayer + a leaf (weeds).
  herbicide: sprayer('#E6F0DA', '#3C6E2F', (
    <path d="M78 70 Q92 60 90 84 Q76 86 78 70 Z" fill="#7BAA4E" stroke="#3C6E2F" strokeWidth="1.3" />
  )),
  // Trigger sprayer + a bug.
  insecticide: sprayer('#F3E3D3', '#8A4A1F', (
    <g>
      <ellipse cx="84" cy="76" rx="6" ry="8" fill="#8A4A1F" />
      <circle cx="84" cy="66" r="3.5" fill="#8A4A1F" />
      <path d="M78 72 L72 68 M78 78 L71 79 M90 72 L96 68 M90 78 L97 79" stroke="#8A4A1F" strokeWidth="1.4" />
    </g>
  )),
  // Trigger sprayer + a mushroom (fungus).
  fungicide: sprayer('#ECE3F1', '#5C3F86', (
    <g>
      <path d="M74 74 Q84 60 94 74 Z" fill="#A88CC4" stroke="#5C3F86" strokeWidth="1.3" />
      <rect x="81.5" y="74" width="5" height="10" rx="1" fill="#F4ECF8" stroke="#5C3F86" strokeWidth="1.2" />
    </g>
  )),
  // Watering can.
  equipment: {
    bg: '#E3E8EC',
    shape: (
      <g>
        <rect x="30" y="44" width="40" height="40" rx="6" fill="#B9C7D1" stroke="#3D5566" strokeWidth="1.8" />
        <path d="M70 52 L90 36 L94 40 L74 60" fill="#B9C7D1" stroke="#3D5566" strokeWidth="1.6" />
        <path d="M38 44 Q50 26 62 44" fill="none" stroke="#3D5566" strokeWidth="3" />
        <rect x="88" y="32" width="8" height="10" rx="2" fill="#3D5566" transform="rotate(-38 92 37)" />
      </g>
    ),
  },
};

function sprayer(bg, ink, extra) {
  return {
    bg,
    shape: (
      <g>
        <rect x="40" y="16" width="18" height="10" rx="2" fill={ink} />
        <path d="M58 18 L70 18 L70 24 L58 24" fill={ink} />
        <rect x="44" y="26" width="10" height="8" fill={ink} />
        <rect x="32" y="34" width="34" height="56" rx="6" fill="#FFFFFF" stroke={ink} strokeWidth="1.8" />
        <rect x="36" y="50" width="26" height="24" rx="2" fill={ink} opacity="0.85" />
        {extra}
      </g>
    ),
  };
}
